// Phase 4 (authz-hardening): route-level permission gate for TenantLayout.
// Renders the route element only when the user holds ANY of the canonical
// permissions (server-resolved set ∪ static role map, via useHasAnyPermission);
// otherwise redirects to the dashboard. This is truth-in-UI — the backend
// remains the enforcement point; this stops honest users from landing on
// pages whose every call would 403.
//
// `orTenantWide` additionally admits a holder of that ONE permission at TENANT
// scope (useHasTenantWidePermission) — for a page with one company-wide surface
// (e.g. the document verification queue) whose other surfaces keep `perms`. The
// page itself must then gate those other surfaces on `perms`.
import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

import { useHasAnyPermission, useHasTenantWidePermission } from './useRbac';

export function RequireAnyPermissionRoute({
  perms,
  orTenantWide,
  children,
}: {
  perms: string[];
  orTenantWide?: string;
  children: ReactNode;
}) {
  const anyOf = useHasAnyPermission(perms);
  const tenantWide = useHasTenantWidePermission(orTenantWide ?? '');
  const allowed = anyOf || (!!orTenantWide && tenantWide);
  if (!allowed) return <Navigate to="../dashboard" replace />;
  return <>{children}</>;
}
